import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Card, Typography, Tag, Space, Avatar, Button, Divider, message, Tooltip, Badge } from 'antd';
import { CalendarOutlined, EditOutlined, DeleteOutlined, ArrowLeftOutlined, EyeOutlined, LikeOutlined, LikeFilled, CrownOutlined } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

// 获取文章数据（支持管理员查看任何文章）
const getPostById = (postId) => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  let targetPost = null;
  let postUserId = null;
  
  // 如果是管理员，搜索所有用户的文章
  if (currentUser.isAdmin) {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      
      if (key.startsWith('userPosts_')) {
        const userId = key.replace('userPosts_', '');
        const userPosts = JSON.parse(localStorage.getItem(key) || '[]');
        const foundPost = userPosts.find(p => p.id === parseInt(postId));
        
        if (foundPost) {
          targetPost = foundPost;
          postUserId = parseInt(userId);
          break;
        }
      }
    }
  } else {
    // 普通用户只查看自己的文章
    const userPosts = JSON.parse(localStorage.getItem(`userPosts_${currentUser.id}`) || '[]');
    targetPost = userPosts.find(p => p.id === parseInt(postId));
    postUserId = currentUser.id;
  }
  
  return { post: targetPost, userId: postUserId };
};

// 更新文章
const updatePost = (postId, updatedData, postUserId) => {
  const userPosts = JSON.parse(localStorage.getItem(`userPosts_${postUserId}`) || '[]');
  const index = userPosts.findIndex(p => p.id === postId);
  
  if (index !== -1) {
    userPosts[index] = { ...userPosts[index], ...updatedData };
    localStorage.setItem(`userPosts_${postUserId}`, JSON.stringify(userPosts));
    return true;
  }
  return false;
};

// 删除文章
const deletePost = (postId, postUserId) => {
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
  
  // 检查权限
  if (currentUser.isAdmin || currentUser.id === postUserId) {
    const userPosts = JSON.parse(localStorage.getItem(`userPosts_${postUserId}`) || '[]');
    const filteredPosts = userPosts.filter(p => p.id !== postId);
    localStorage.setItem(`userPosts_${postUserId}`, JSON.stringify(filteredPosts));
    
    // 删除点赞记录
    const userLikes = JSON.parse(localStorage.getItem(`userLikes_${postUserId}`) || '{}');
    delete userLikes[postId];
    localStorage.setItem(`userLikes_${postUserId}`, JSON.stringify(userLikes));
    
    return true;
  }
  
  return false;
};

const PostDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [postUserId, setPostUserId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [currentUser, setCurrentUser] = useState({});
  
  // 使用 useRef 来跟踪是否已经增加过浏览量
  const hasIncreasedViews = useRef(false);
  
  useEffect(() => {
    // 获取当前用户
    const user = JSON.parse(localStorage.getItem('currentUser') || '{}');
    setCurrentUser(user);
    
    // 重置标志位
    hasIncreasedViews.current = false;
    
    // 获取文章
    const { post: foundPost, userId } = getPostById(parseInt(id));
    
    if (foundPost) {
      // 只在第一次渲染时增加浏览量
      if (!hasIncreasedViews.current) {
        hasIncreasedViews.current = true;
        
        // 增加浏览量
        const updatedPost = {
          ...foundPost,
          views: (foundPost.views || 0) + 1
        };
        
        // 更新文章
        updatePost(foundPost.id, updatedPost, userId);
        setPost(updatedPost);
        setPostUserId(userId);
        setLikesCount(updatedPost.likes || 0);
        
        // 检查当前用户是否已经点赞
        const userLikes = JSON.parse(localStorage.getItem(`userLikes_${userId}`) || '{}');
        setLiked(!!userLikes[foundPost.id]);
      } else {
        // 如果已经增加过，直接使用原数据
        setPost(foundPost);
        setPostUserId(userId);
        setLikesCount(foundPost.likes || 0);
        
        const userLikes = JSON.parse(localStorage.getItem(`userLikes_${userId}`) || '{}');
        setLiked(!!userLikes[foundPost.id]);
      }
    }
    
    setLoading(false);
    
    // 清理函数，重置标志位
    return () => {
      hasIncreasedViews.current = false;
    };
  }, [id]);

  const handleLike = () => {
    if (!post || !postUserId) return;
    
    const userLikes = JSON.parse(localStorage.getItem(`userLikes_${postUserId}`) || '{}');
    const alreadyLiked = !!userLikes[post.id];
    
    let newLikesCount;
    let newLiked;
    
    if (alreadyLiked) {
      // 取消点赞
      newLikesCount = Math.max(0, (post.likes || 0) - 1);
      newLiked = false;
      message.success('已取消点赞');
    } else {
      // 点赞
      newLikesCount = (post.likes || 0) + 1;
      newLiked = true;
      message.success('点赞成功！');
    }
    
    // 更新文章点赞数
    const updatedPost = {
      ...post,
      likes: newLikesCount
    };
    
    updatePost(post.id, updatedPost, postUserId);
    setPost(updatedPost);
    setLikesCount(newLikesCount);
    setLiked(newLiked);
    
    // 更新点赞记录（使用文章所属用户的点赞记录）
    userLikes[post.id] = newLiked;
    localStorage.setItem(`userLikes_${postUserId}`, JSON.stringify(userLikes));
  };

  const handleDelete = () => {
    const confirmMessage = currentUser.isAdmin && currentUser.id !== postUserId
      ? `确定要删除用户 "${post.author?.username || '未知用户'}" 的日记吗？`
      : '确定要删除这篇日记吗？';
    
    if (window.confirm(confirmMessage)) {
      const success = deletePost(post.id, postUserId);
      if (success) {
        message.success('日记删除成功！');
        setTimeout(() => {
          navigate('/');
        }, 500);
      } else {
        message.error('删除失败，没有权限！');
      }
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>加载中...</div>;
  }

  if (!post) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Title level={3}>日记不存在</Title>
        <Link to="/">
          <Button type="primary">返回首页</Button>
        </Link>
      </div>
    );
  }

  const isOwner = currentUser.id === postUserId;
  const canEdit = isOwner || currentUser.isAdmin;
  const canDelete = isOwner || currentUser.isAdmin;

  return (
    <div>
      <Space style={{ marginBottom: 20 }}>
        <Link to="/">
          <Button icon={<ArrowLeftOutlined />} type="text">
            返回列表
          </Button>
        </Link>
        
        {canEdit && (
          <Link to={`/write/${post.id}`}>
            <Button icon={<EditOutlined />} type="primary">
              编辑
            </Button>
          </Link>
        )}
        
        {canDelete && (
          <Button 
            icon={<DeleteOutlined />} 
            danger
            onClick={handleDelete}
          >
            删除
          </Button>
        )}
        
        {currentUser.isAdmin && !isOwner && (
          <Badge 
            count="管理员操作" 
            style={{ 
              backgroundColor: '#ff9ebb', 
              marginLeft: 10,
              fontSize: '12px',
              fontWeight: 'normal'
            }} 
          />
        )}
      </Space>
      
      <Card loading={loading}>
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Title level={2} style={{ marginBottom: 8, flex: 1 }}>
                {post.title}
              </Title>
              
              {currentUser.isAdmin && (
                <Space direction="vertical" align="end">
                  <Space size={4}>
                    {post.author?.isAdmin && (
                      <Tooltip title="管理员">
                        <CrownOutlined style={{ color: 'gold' }} />
                      </Tooltip>
                    )}
                    <Avatar size="small" style={{ 
                      backgroundColor: post.author?.isAdmin ? '#fff9db' : '#fde3cf',
                      border: post.author?.isAdmin ? '1px solid gold' : 'none'
                    }}>
                      {post.author?.avatar || '👤'}
                    </Avatar>
                    <Text strong>{post.author?.username || '未知用户'}</Text>
                    {!isOwner && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        (用户ID: {postUserId})
                      </Text>
                    )}
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {isOwner ? '您的日记' : '其他用户的日记'}
                  </Text>
                </Space>
              )}
            </div>
            
            <Space split={<Text type="secondary">·</Text>} style={{ marginBottom: 16 }}>
              {!currentUser.isAdmin && (
                <Space size={4}>
                  <Avatar size="small" style={{ backgroundColor: '#fde3cf' }}>
                    {post.author?.avatar || currentUser.avatar || '👤'}
                  </Avatar>
                  <Text strong>{post.author?.username || currentUser.username || '我'}</Text>
                </Space>
              )}
              
              <Space size={4}>
                <CalendarOutlined />
                <Text>{post.createdAt ? new Date(post.createdAt).toLocaleString() : '未知时间'}</Text>
              </Space>
              
              <Tooltip title="浏览量">
                <Space size={4}>
                  <EyeOutlined />
                  <Text>{post.views || 0}</Text>
                </Space>
              </Tooltip>
              
              <Tooltip title={liked ? "已点赞" : "点赞"}>
                <Button 
                  type="text" 
                  size="small" 
                  style={{ padding: '0 4px', height: 'auto' }}
                  onClick={handleLike}
                >
                  <Space size={4}>
                    {liked ? <LikeFilled style={{ color: '#ff4d4f' }} /> : <LikeOutlined />}
                    <Text>{likesCount}</Text>
                  </Space>
                </Button>
              </Tooltip>
            </Space>
            
            {post.tags && post.tags.length > 0 && (
              <Space wrap>
                {post.tags.map(tag => (
                  <Tag key={tag} color="pink" style={{ fontSize: 14 }}>
                    {tag}
                  </Tag>
                ))}
              </Space>
            )}
          </div>
          
          <Divider />
          
          <Paragraph style={{ fontSize: 16, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
            {post.content}
          </Paragraph>
          
          <Divider />
          
          <div style={{ textAlign: 'center' }}>
            <Space>
              <Text type="secondary">
                最后更新于 {post.createdAt ? new Date(post.createdAt).toLocaleString() : '未知时间'}
              </Text>
              <Text type="secondary">·</Text>
              <Text type="secondary">
                浏览量 {post.views || 0}
              </Text>
              <Text type="secondary">·</Text>
              <Text type="secondary">
                点赞 {likesCount}
              </Text>
              {currentUser.isAdmin && !isOwner && (
                <>
                  <Text type="secondary">·</Text>
                  <Text type="secondary">
                    作者：{post.author?.username || '未知用户'}
                  </Text>
                </>
              )}
            </Space>
          </div>
        </Space>
      </Card>
    </div>
  );
};

export default PostDetail;